// SPDX-License-Identifier: BUSL-1.1

/**
 * Create Flag dialog — creates a new feature flag with provider and type.
 *
 */

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api-client';
import { showSuccess, showApiError } from '@/lib/toast';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import {
  Form, FormControl, FormField, FormItem, FormLabel, FormMessage,
} from '@/components/ui/form';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const PROVIDER_OPTIONS = ['internal', 'posthog'] as const;
const CONFIG_TYPE_OPTIONS = ['feature_flag', 'kill_switch', 'experiment'] as const;

const PROVIDER_LABELS: Record<string, string> = {
  internal: 'Internal',
  posthog: 'PostHog',
};

const CONFIG_TYPE_LABELS: Record<string, string> = {
  feature_flag: 'Feature Flag',
  kill_switch: 'Kill Switch',
  experiment: 'Experiment',
};

/* ------------------------------------------------------------------ */
/*  Zod schema                                                         */
/* ------------------------------------------------------------------ */

const createFlagSchema = z.object({
  key: z
    .string()
    .min(1, 'Key is required')
    .regex(
      /^[a-z0-9]+(-[a-z0-9]+)*$/,
      'Lowercase alphanumeric and hyphens only (e.g. "my-flag")',
    ),
  description: z.string().optional(),
  enabled: z.boolean(),
  provider: z.enum(PROVIDER_OPTIONS),
  configType: z.enum(CONFIG_TYPE_OPTIONS),
});

type CreateFlagValues = z.infer<typeof createFlagSchema>;

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function CreateFlagDialog() {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();

  const form = useForm<CreateFlagValues>({
    resolver: zodResolver(createFlagSchema),
    defaultValues: {
      key: '',
      description: '',
      enabled: false,
      provider: 'internal',
      configType: 'feature_flag',
    },
  });

  const mutation = useMutation({
    mutationFn: (values: CreateFlagValues) =>
      apiFetch('/flags', { method: 'POST', body: JSON.stringify(values) }),
    onSuccess: (_data, variables) => {
      showSuccess(`Flag "${variables.key}" created`);
      queryClient.invalidateQueries({ queryKey: ['flags'] });
      form.reset();
      setOpen(false);
    },
    onError: (err) => showApiError(err),
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger render={<Button variant="secondary" />}>
        Create Flag
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Feature Flag</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit((v) => mutation.mutate(v))}
            className="flex flex-col gap-4"
          >
            <FormField
              control={form.control}
              name="key"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Flag Key</FormLabel>
                  <FormControl>
                    <Input placeholder="my-feature-flag" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Input placeholder="Optional description" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="provider"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Provider</FormLabel>
                  <FormControl>
                    <Select
                      value={field.value}
                      onValueChange={(v) => v && field.onChange(v)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select provider" />
                      </SelectTrigger>
                      <SelectContent>
                        {PROVIDER_OPTIONS.map((p) => (
                          <SelectItem key={p} value={p}>
                            {PROVIDER_LABELS[p]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="configType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Config Type</FormLabel>
                  <FormControl>
                    <Select
                      value={field.value}
                      onValueChange={(v) => v && field.onChange(v)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {CONFIG_TYPE_OPTIONS.map((t) => (
                          <SelectItem key={t} value={t}>
                            {CONFIG_TYPE_LABELS[t]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="enabled"
              render={({ field }) => (
                <FormItem className="flex items-center gap-2">
                  <FormControl>
                    <Checkbox
                      name={field.name}
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      onBlur={field.onBlur}
                      ref={field.ref}
                    />
                  </FormControl>
                  <FormLabel className="!mt-0">Enable on creation</FormLabel>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" disabled={mutation.isPending} className="mt-2">
              {mutation.isPending ? 'Creating...' : 'Create Flag'}
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
